import type { FreightHazmatInfo, LtlCommodity } from '../types/ltl';

export const RequiredHazmatFields = {
  identificationNumber: 'identificationNumber',
  properShippingName: 'properShippingName',
  hazardClass: 'hazardClass',
  packingGroup: 'packingGroup',
  emergencyContactName: 'emergencyContactName',
  emergencyPhone: 'emergencyPhone',
} as const;

export type RequiredHazmatField = (typeof RequiredHazmatFields)[keyof typeof RequiredHazmatFields];

const isRequiredFieldFilled = (info: FreightHazmatInfo, field: RequiredHazmatField): boolean =>
  field === 'hazardClass' ? !!info.hazardClass?.length : !!info[field];

/**
 * Returns true when any hazmat field (required or not) has a meaningful value.
 * `flashpointTemperatureF` uses != null rather than !! so that 0 °F counts as filled.
 * Used to determine when required-field validation should activate.
 */
export const isAnyHazmatFieldFilled = (info: FreightHazmatInfo | null): boolean =>
  !!info &&
  !!(
    info.identificationNumber ||
    info.properShippingName ||
    info.hazardClass?.length ||
    info.packingGroup ||
    info.emergencyContactName ||
    info.emergencyPhone ||
    info.emergencyResponseReference ||
    info.flashpointTemperatureF != null ||
    info.additionalDetails
  );

/**
 * Keys off the explicit `isHazmat` flag — no validation when false, even if hazmat fields are
 * partially filled.  When `isHazmat` is true but `hazmatInfo` is null (e.g. right after the
 * checkbox is ticked, or a saved plan stored `isHazmat: true` with a null info), all six required
 * fields are considered missing.
 *
 * Intentionally used only by the booking and "complete" tiers of the validation registry
 * (`getHandlingUnitErrors`), **not** the quote tier (`getHandlingUnitsRateErrors`). Carriers rate
 * on dimensions, weight, and freight class — an incomplete hazmat block must not prevent rates from
 * loading while the user is still filling in hazmat details.
 */
export const getMissingRequiredHazmatFieldsForCommodity = (
  commodity: Pick<LtlCommodity, 'isHazmat' | 'hazmatInfo'>,
): Set<RequiredHazmatField> => {
  if (!commodity.isHazmat) return new Set();
  if (!commodity.hazmatInfo) return new Set(Object.values(RequiredHazmatFields));
  return new Set(Object.values(RequiredHazmatFields).filter((f) => !isRequiredFieldFilled(commodity.hazmatInfo!, f)));
};
